import * as React from 'react'
import { cn } from '../../lib/utils'
import { DetailSectionDefaultsContext } from '../core/detail-section'

/**
 * DetailPanel — the sidebar shell of a detail page: a bordered, scrollable white
 * card that stacks {@link DetailSection}s (Summary, Related Companies, Contacts,
 * Tags …). It's just the chrome — width, padding, border, and a scroll region —
 * so the same panel frames a deal, a company, or a contact; the sections and
 * their bodies (an `InfoList`, a `Tree`, chip clusters) are composed as
 * `children`.
 *
 *   <DetailPanel>
 *     <DetailSection title="Summary"><InfoList>…</InfoList></DetailSection>
 *     <DetailSection title="Related Companies" count={2}>
 *       <Tree nodes={…} onAdd={…} addLabel="Add company" />
 *     </DetailSection>
 *   </DetailPanel>
 *
 * Defaults to the 340px column the app uses; override with a `w-*` class. The
 * card scrolls its own content (`fbui-scroll`) when the parent constrains its
 * height (`h-full` in the page layout); unconstrained, it grows to fit.
 *
 * Sections inside a panel get the in-header `rule` and a heading `level` of 2 by
 * default (via context) so the whole panel reads consistently under a page `h1`;
 * set `rule={false}` / `level` on a section to opt out.
 */

export interface DetailPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export const DetailPanel = React.forwardRef<HTMLDivElement, DetailPanelProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // `[&>*+*]:mt-2` spaces stacked sections so the gap *between* sections
        // clearly reads larger than the gap between rows *within* one — grouping.
        'fbui-scroll flex w-[340px] flex-col overflow-y-auto rounded-lg border border-fbui-gray-200 bg-fbui-base-white p-4 shadow-fbui-sm [&>*+*]:mt-2',
        className
      )}
      {...props}
    >
      <DetailSectionDefaultsContext.Provider value={{ rule: true, level: 2 }}>
        {children}
      </DetailSectionDefaultsContext.Provider>
    </div>
  )
)
DetailPanel.displayName = 'DetailPanel'
